import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemChip, ActionChip } from '../pills';
import { OptToken } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { ConfirmDialog } from '../ConfirmDialog';
import { useListFetch } from '../useListFetch';
import { useWriteFeedback } from '../useWriteFeedback';

/**
 * LeasesExpiringCard — Part 4 of the real-estate module (realestate-dashboard-prd.md §4). Leases ending
 * within 30 days (GET /api/properties/leases-expiring), soonest-first, each backed by a real Lease
 * (AC4.1). renewalState is TWO values only — 'not started' | 'renewal sent' (§4.1) — a one-way
 * transition: once sent, the send control is gone for good, no "Mark-replied", no third state.
 *
 * "Send renewal" REUSES Part 1's shared WhatsApp-reachability helper VERBATIM (reachability.ts) and
 * mirrors RentRollCard's send-action treatment exactly:
 *   - sendable      → a tappable "Send renewal" ActionChip (confirm-first).
 *   - window-closed → a NON-interactive note, no tap target (an action you can't take must not look
 *                     tappable; it is NOT a disabled button — the window only reopens when the tenant
 *                     re-initiates).
 *   - not-connected → no per-row chrome; the single page-level banner (PortfolioTab, Part 1) discloses
 *                     the one account-level cause.
 * Reachability (and the send control entirely) is only ever shown while renewalState==='not started'.
 */

type RenewalState = 'not started' | 'renewal sent';
type Reachability = 'sendable' | 'window-closed' | 'not-connected';

interface LeaseExpiringRow {
  leaseId: string;
  unitId: string;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  tenantLeadId: string;
  tenantName: string | null;
  endDate: string;
  rent: number;
  renewalState: RenewalState;
  reachability: Reachability | null;
  renewalSentAt: string | null;
}
interface LeasesExpiring {
  multiProperty: boolean;
  whatsappConnected: boolean;
  rows: LeaseExpiringRow[];
}

const RENEWAL_TOKEN: Record<RenewalState, OptToken> = { 'not started': 'slate', 'renewal sent': 'emerald' };

function endsLabel(iso: string, t: (k: string, o?: any) => string): string {
  const days = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000);
  return days <= 0 ? t('portfolio.leasesExpiring.endsToday') : t('portfolio.leasesExpiring.endsIn', { count: days });
}

function sentLabel(iso: string, t: (k: string, o?: any) => string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return t('portfolio.leasesExpiring.renewal.sentJustNow');
  if (hours < 48) return t('portfolio.leasesExpiring.renewal.sentHours', { count: hours });
  return t('portfolio.leasesExpiring.renewal.sentDays', { count: Math.floor(hours / 24) });
}

const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const LeasesExpiringCard: React.FC = () => {
  const { t } = useTranslation('common');
  const [refreshKey, setRefreshKey] = useState(0);
  const { status, data, retry } = useListFetch<LeasesExpiring>('/api/properties/leases-expiring', { refreshKey });
  const feedback = useWriteFeedback();
  const [confirmRow, setConfirmRow] = useState<LeaseExpiringRow | null>(null);
  const [busyLeaseId, setBusyLeaseId] = useState<string | null>(null);

  const refetch = () => setRefreshKey((k) => k + 1);
  const multi = data?.multiProperty ?? false;

  const doSend = async (row: LeaseExpiringRow) => {
    setConfirmRow(null);
    setBusyLeaseId(row.leaseId);
    try {
      const res = await fetch(`/api/leases/${row.leaseId}/send-renewal`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const outcome: string = json?.status ?? (res.ok ? 'sent' : 'error');
      // Same honesty convention as RentRollCard: success has no toast (the row's own state flip + the
      // "Renewal sent" trace IS the feedback); a window_closed/not_connected refusal just refetches to
      // the current honest state (also covers the race where the window closed between render and
      // tap). already_sent (the §4.1 race — sent from another tab/session) likewise refetches so the
      // row flips to its sent state with no further send control, rather than retrying a stale tap.
      if (outcome === 'sent') refetch();
      else if (outcome === 'refused') refetch();
      else if (outcome === 'already_sent') { feedback.fail(t('portfolio.leasesExpiring.renewal.alreadySent')); refetch(); }
      else if (outcome === 'no_phone') feedback.fail(t('portfolio.leasesExpiring.renewal.noPhone'));
      else feedback.fail(t('portfolio.leasesExpiring.renewal.failed'));
    } catch {
      feedback.fail(t('portfolio.leasesExpiring.renewal.failed'));
    } finally {
      setBusyLeaseId(null);
    }
  };

  // Action cell for one row. The send control exists ONLY while renewalState==='not started' (§4.1) —
  // once sent, this renders the trace (or a dash) and nothing else, permanently.
  const renderAction = (row: LeaseExpiringRow) => {
    if (row.renewalState === 'renewal sent') {
      return row.renewalSentAt ? (
        <div className="text-[11px] text-text-muted" data-testid="portfolio-renewal-sent-trace">{sentLabel(row.renewalSentAt, t)}</div>
      ) : <span className="text-text-muted">—</span>;
    }
    if (row.reachability === 'sendable') {
      return (
        <ActionChip
          label={t('portfolio.leasesExpiring.renewal.send')}
          onAct={() => setConfirmRow(row)}
          disabled={busyLeaseId === row.leaseId}
          testId="portfolio-renewal-send"
        />
      );
    }
    if (row.reachability === 'window-closed') {
      // NON-interactive note — no tap target at all. A SystemChip is structurally not a button.
      return <SystemChip label={t('portfolio.leasesExpiring.renewal.closed')} tone="muted" testId="portfolio-renewal-closed" />;
    }
    // not-connected — the page-level banner covers it; no per-row chrome.
    return <span className="text-text-muted">—</span>;
  };

  return (
    <div className="bg-surface border border-border-neutral rounded-xl overflow-hidden" data-testid="portfolio-leases-expiring">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-neutral">
        <h3 className="text-sm font-bold text-text-strong m-0">{t('portfolio.leasesExpiring.title')}</h3>
      </div>

      <ListStateShell status={status} testIdBase="portfolio-leases-expiring" onRetry={retry}>
        {!data || data.rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-text-muted" data-testid="portfolio-leases-expiring-empty">{t('portfolio.leasesExpiring.empty')}</div>
        ) : (
          <div className="divide-y divide-border-neutral">
            {data.rows.map((row) => (
              <div key={row.leaseId} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="portfolio-lease-expiring-row">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-text-strong truncate">
                    {row.unitLabel}{multi ? ` · ${row.propertyName}` : ''} · {row.tenantName ?? '—'}
                  </div>
                  <div className="text-xs text-text-muted mt-0.5" dir="ltr">{endsLabel(row.endDate, t)} · {fmtMoney(row.rent)}</div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <SystemChip
                    label={row.renewalState === 'renewal sent' ? t('portfolio.leasesExpiring.status.sent') : t('portfolio.leasesExpiring.status.notStarted')}
                    token={RENEWAL_TOKEN[row.renewalState]}
                  />
                  {renderAction(row)}
                </div>
              </div>
            ))}
          </div>
        )}
      </ListStateShell>

      {confirmRow && (
        <ConfirmDialog
          title={t('portfolio.leasesExpiring.renewal.confirmTitle')}
          message={t('portfolio.leasesExpiring.renewal.confirm', { tenant: confirmRow.tenantName ?? '—' })}
          confirmLabel={t('portfolio.leasesExpiring.renewal.send')}
          onConfirm={() => doSend(confirmRow)}
          onClose={() => setConfirmRow(null)}
        />
      )}
      {feedback.node}
    </div>
  );
};
